import React from 'react'
import { Form, Formik } from 'formik'
import { User } from '../../../api/users'
import { Organization } from '../../../api/organizations'
import {
  ContractualScheduleEntryFormValue,
  createNewContractualScheduleEntry,
  transformContractualSchedulesToAPI,
  canApplyContractualScheduleToWorker,
} from './user-form/contractual-schedules-utils'
import { validateContractualSchedules } from './user-form/contractual-schedules-validation'
import ContractualSchedulesSection from './user-form/contractual-schedules-section'
import { useUpdateWorkerMutation } from '../../../queries/users'
import { Button, Element, Heading, Icon, Message } from 'react-bulma-components'
import { CheckIcon } from '../../icons'
import { BanIcon } from '../../icons'
import { RequestButton, RequestMessage } from '../../request-components/request-components'
import { getWorkerAndOrganizationAssociation } from '../../../utils/users'
import { getUserLabel } from '../../../utils/users'

interface BulkContractualSchedulesFormProps {
  users: User[]
  organization: Organization
  onSuccess?: () => void
}

interface BulkContractualSchedulesFormValues {
  contractualSchedules: ContractualScheduleEntryFormValue[]
}

/**
 * Form component for applying contractual schedules to multiple workers
 */
const BulkContractualSchedulesForm: React.FC<BulkContractualSchedulesFormProps> = ({
  users,
  organization,
  onSuccess,
}) => {
  const updateMutation = useUpdateWorkerMutation()

  // Check applicability for each worker
  const getApplicabilityStatus = (
    user: User,
    schedule: ContractualScheduleEntryFormValue,
  ): { applicable: boolean; reason?: string } => {
    return canApplyContractualScheduleToWorker(user, schedule, organization)
  }

  const handleSubmit = async (values: BulkContractualSchedulesFormValues) => {
    const { contractualSchedules } = values

    if (!contractualSchedules || contractualSchedules.length === 0) {
      return
    }

    const scheduleToApply = contractualSchedules[0] // Use first schedule entry
    if (!scheduleToApply) return

    // Get applicable workers
    const applicableWorkers = users.filter(user => {
      const status = getApplicabilityStatus(user, scheduleToApply)
      return status.applicable
    })

    // Apply schedule to each applicable worker
    // Replace existing entry for workers without schedule (applicable workers)
    const updatePromises = applicableWorkers.map(async user => {
      const association = getWorkerAndOrganizationAssociation(user, organization)
      if (!association) return

      // Transform the new schedule to API format
      const newScheduleEntry = transformContractualSchedulesToAPI([scheduleToApply])[0]
      if (!newScheduleEntry) return

      // Replace the existing entry (if any) with the new one
      // Since applicable workers don't have schedule entries with schedule !== null,
      // we can simply replace all entries with the new one
      const allSchedules = [newScheduleEntry]

      return updateMutation.mutateAsync({
        id: user._id,
        contractualSchedules: allSchedules,
      })
    })

    await Promise.all(updatePromises)
    if (onSuccess) {
      onSuccess()
    }
  }

  return (
    <Formik<BulkContractualSchedulesFormValues>
      initialValues={{
        contractualSchedules: [createNewContractualScheduleEntry()],
      }}
      validate={values => {
        const errors: any = {}
        if (values.contractualSchedules) {
          const scheduleErrors = validateContractualSchedules(values.contractualSchedules)
          Object.assign(errors, scheduleErrors)
        }
        return errors
      }}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting }) => {
        const schedule = values.contractualSchedules?.[0]
        const applicabilityStatuses = users.map(user => ({
          user,
          status: schedule ? getApplicabilityStatus(user, schedule) : { applicable: false },
        }))

        const applicableCount = applicabilityStatuses.filter(s => s.status.applicable).length
        const nonApplicableCount = users.length - applicableCount

        return (
          <Form>
            <Element mb={4}>
              <Heading size={6} mb={3}>
                Travailleurs sélectionnés ({users.length})
              </Heading>
              <Element
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #e8e8e8',
                  borderRadius: '4px',
                  padding: '0.75rem',
                }}
              >
                {applicabilityStatuses.map(({ user, status }) => (
                  <Element
                    key={user._id}
                    display="flex"
                    alignItems="center"
                    mb={2}
                    style={{ gap: '0.5rem' }}
                  >
                    {status.applicable ? (
                      <Icon color="success">
                        <CheckIcon />
                      </Icon>
                    ) : (
                      <Icon color="danger">
                        <BanIcon />
                      </Icon>
                    )}
                    <Element renderAs="span" style={{ flex: 1 }}>
                      {getUserLabel(user)}
                    </Element>
                    {!status.applicable && status.reason && (
                      <Element renderAs="span" textSize={7} textColor="grey">
                        {status.reason}
                      </Element>
                    )}
                  </Element>
                ))}
              </Element>
              {nonApplicableCount > 0 && (
                <Message color="warning" mt={3}>
                  <Message.Body>
                    {nonApplicableCount} travailleur{nonApplicableCount > 1 ? 's' : ''} ne
                    {nonApplicableCount > 1 ? ' peuvent' : ' peut'} pas recevoir cet horaire.
                    {applicableCount > 0 && (
                      <span>
                        {' '}
                        L'horaire sera appliqué uniquement aux {applicableCount} travailleur
                        {applicableCount > 1 ? 's' : ''} applicable{applicableCount > 1 ? 's' : ''}.
                      </span>
                    )}
                  </Message.Body>
                </Message>
              )}
            </Element>

            <Element mb={4}>
              <ContractualSchedulesSection
                name="contractualSchedules"
                hasWorkerEverHadTimeSlots={false}
              />
            </Element>

            <Element display="flex" justifyContent="flex-end" style={{ gap: '0.5rem' }}>
              <Button
                onClick={() => {
                  // Close modal - handled by parent
                }}
                type="button"
              >
                Annuler
              </Button>
              <RequestButton
                color="primary"
                type="submit"
                mutation={updateMutation}
                disabled={applicableCount === 0 || isSubmitting}
              >
                Appliquer ({applicableCount})
              </RequestButton>
            </Element>
            <RequestMessage mutation={updateMutation} />
          </Form>
        )
      }}
    </Formik>
  )
}

export default BulkContractualSchedulesForm
